import React, {useCallback, useRef} from 'react'
import {useEditor, EditorContent} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import ResizableImage from './extensions/ResizableImage'
import {Color} from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'
import FontFamily from '@tiptap/extension-font-family'
import Superscript from '@tiptap/extension-superscript'
import Underline from '@tiptap/extension-underline'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import FontSize from './extensions/FontSize'
import MediaEmbed from './extensions/MediaEmbed'
import HtmlEmbed from './extensions/HtmlEmbed'
import Indent from './extensions/Indent'
import ObvioVariable from './extensions/ObvioVariable'
import CustomButton from './extensions/CustomButton'
import SelectionHighlight from './extensions/SelectionHighlight'
import MenuBar from './MenuBar'
import {useUploadAsset} from 'lib/asset'
import {ToolbarItem, ObvioVariable as ObvioVariableType} from './types'
import {defaultToolbar} from './toolbar'
import useEditorTheme from './useEditorTheme'
import './html-editor.css'

/**
 * Custom serializer that injects raw HTML embed content into the output.
 * TipTap's DOMOutputSpec can't output raw innerHTML, so we post-process
 * the getHTML() output to replace empty raw-html-embed divs with their content.
 */
function serializeHTML(editor: ReturnType<typeof useEditor>): string {
  if (!editor) return ''

  const container = document.createElement('div')
  container.innerHTML = editor.getHTML()

  const placeholders = container.querySelectorAll('div.raw-html-embed')
  let index = 0

  editor.state.doc.descendants((node) => {
    if (node.type.name === 'htmlEmbed' && placeholders[index]) {
      placeholders[index].innerHTML = node.attrs.html || ''
      index++
    }
  })

  return container.innerHTML
}

export type HTMLEditorProps = {
  data: string
  onChange: (value: string) => void
  className?: string
  disabled?: boolean
  toolbar?: ToolbarItem[]
  customLinks?: string[]
  variables?: ObvioVariableType[]
}

export default function HTMLEditor(props: HTMLEditorProps) {
  const {data, onChange, className, disabled} = props
  const toolbar = props.toolbar || defaultToolbar
  const lastEmittedValue = useRef(data)
  const uploadAsset = useUploadAsset()
  const {theme, setTheme} = useEditorTheme()

  const handleImageUpload = useCallback(
    async (file: File) => {
      try {
        const asset = await uploadAsset(file)
        editor?.chain().focus().setImage({src: asset.file.url}).run()
      } catch (error) {
        // Upload failed - silently ignore
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Walk pasted/dropped HTML, upload any inline data:image/* sources, and
  // rewrite their src to the resulting asset URL so we don't persist base64
  // blobs in the post body.
  //
  // Fixes a bug where a huge inline image was causing the DB read to fail.
  const uploadInlineImages = useCallback(
    async (html: string): Promise<string> => {
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const inlineImages = Array.from(
        doc.querySelectorAll('img[src^="data:image/"]'),
      ) as HTMLImageElement[]
      if (inlineImages.length === 0) return html

      await Promise.all(
        inlineImages.map(async (img) => {
          const src = img.getAttribute('src')
          if (!src) return
          try {
            const blob = await fetch(src).then((r) => r.blob())
            const ext = blob.type.split('/')[1]?.split('+')[0] || 'png'
            const file = new File(
              [blob],
              `pasted-${Date.now()}-${Math.random()
                .toString(36)
                .slice(2, 8)}.${ext}`,
              {type: blob.type},
            )
            const asset = await uploadAsset(file)
            img.setAttribute('src', asset.file.url)
          } catch {
            // Leave the data URI in place so the image isn't lost
          }
        }),
      )

      return doc.body.innerHTML
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      TextStyle,
      FontFamily,
      FontSize,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph', 'listItem'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
        },
      }),
      ResizableImage.configure({
        inline: true,
      }),
      Superscript,
      Underline,
      Table.configure({
        resizable: false,
      }),
      TableRow,
      TableCell,
      TableHeader,
      MediaEmbed,
      HtmlEmbed,
      Indent,
      ObvioVariable,
      CustomButton,
      SelectionHighlight,
    ],
    content: data || '',
    editable: !disabled,
    onUpdate: ({editor: updatedEditor}) => {
      const html = serializeHTML(updatedEditor)
      if (html !== lastEmittedValue.current) {
        lastEmittedValue.current = html
        onChange(html)
      }
    },
    editorProps: {
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files
        if (files && files.length > 0) {
          const imageFiles = Array.from(files).filter((f) =>
            f.type.startsWith('image/'),
          )
          if (imageFiles.length > 0) {
            event.preventDefault()
            imageFiles.forEach((file) => handleImageUpload(file))
            return true
          }
        }
        const html = event.dataTransfer?.getData('text/html')
        if (html && html.includes('data:image/')) {
          event.preventDefault()
          const dropPos = view.posAtCoords({
            left: (event as DragEvent).clientX,
            top: (event as DragEvent).clientY,
          })?.pos
          uploadInlineImages(html).then((processed) => {
            const chain = editor?.chain().focus()
            if (!chain) return
            if (dropPos !== undefined) {
              chain.insertContentAt(dropPos, processed).run()
            } else {
              chain.insertContent(processed).run()
            }
          })
          return true
        }
        return false
      },
      handlePaste: (view, event) => {
        const files = event.clipboardData?.files
        if (files && files.length > 0) {
          const imageFiles = Array.from(files).filter((f) =>
            f.type.startsWith('image/'),
          )
          if (imageFiles.length > 0) {
            event.preventDefault()
            imageFiles.forEach((file) => handleImageUpload(file))
            return true
          }
        }
        const html = event.clipboardData?.getData('text/html')
        if (html && html.includes('data:image/')) {
          event.preventDefault()
          uploadInlineImages(html).then((processed) => {
            editor?.chain().focus().insertContent(processed).run()
          })
          return true
        }
        return false
      },
    },
  })

  // Sync external data changes (e.g. form reset)
  React.useEffect(() => {
    if (!editor) {
      return
    }

    if (data !== lastEmittedValue.current) {
      lastEmittedValue.current = data
      editor.commands.setContent(data || '', false)
    }
  }, [data, editor])

  if (!editor) {
    return null
  }

  const themeClass =
    theme === 'Dark'
      ? 'html-editor--dark'
      : theme === 'Light'
      ? 'html-editor--light'
      : ''

  return (
    <div className={`html-editor ${themeClass} ${className || ''}`}>
      <MenuBar
        editor={editor}
        onImageUpload={handleImageUpload}
        toolbar={toolbar}
        variables={props.variables}
        customLinks={props.customLinks}
        theme={theme}
        onThemeChange={setTheme}
      />
      <EditorContent editor={editor} />
    </div>
  )
}
